import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie';
import { CatalogService } from './catalog/catalog.service';
import { RestService, ApiQueryParams } from './lib/RestService';
import { BitrixFile } from './lib/BitrixFile';
import { setQueryParams } from './lib/helpers';

export class CatalogSection {
  ID:	string;
  SORT:	number;
  CODE:	string;
  NAME:	string;
  XML_ID:	number;
  ACTIVE:	string;
  PICTURE: BitrixFile;
  IBLOCK_ID: number;
  CREATED_BY:	number;
  DESCRIPTION: string;
  TIMESTAMP_X: Date;
  MODIFIED_BY: number;
  DATE_CREATE: Date;
  LEFT_MARGIN: number;
  DEPTH_LEVEL: number;
  RIGHT_MARGIN: number;
  GLOBAL_ACTIVE: string;
  DETAIL_PICTURE: BitrixFile;
  ELEMENTS_COUNT: number | string;
  IBLOCK_SECTION_ID: string;
  IBLOCK_ROOT_SECTION_ID?: string;
  SELECTED?: boolean;
  SUBSECTIONS?: CatalogSection[];
  UF_FILTER_TAGS?: {
    CODE:	string;
    NAME: string;
    PROPERTY_JSON: string;
    DETAIL_TEXT: string;
  }[];
  UF_PARENT_SECTION: string;
}

@Injectable()
export class CatalogSectionService extends RestService {
  sections: CatalogSection[] = [];
  promise: Promise<CatalogSection[]> = new Promise(() => {});

  private apiUrl = `${environment.apiUrl}shop/CatalogSection/`;

  constructor(
    private http: HttpClient,
    private cookie: CookieService,
    private catalogService: CatalogService,
  ) {
    super();

    // do not update if there is a cookie, because update through the filter service
    if (!(this.cookie.get('target') || this.cookie.get('activity'))) this.updateSections();
  }

  public async updateSections(tags?: string[]) {
    this.promise = this.get(tags);
    this.sections = await this.promise;
    return this.sections;
  }

  public async get(tags?: string[]): Promise<CatalogSection[] | any> {
    const level2UrlFilter: any = {DEPTH_LEVEL: '2', ACTIVE: 'Y', GLOBAL_ACTIVE: 'Y'};
    if (tags && tags.length > 0) level2UrlFilter.UF_TAGS = tags;

    const level1Url = setQueryParams(this.apiUrl, {
      filter: {DEPTH_LEVEL: '1', ACTIVE: 'Y', GLOBAL_ACTIVE: 'Y'},
      navParams: {nPageSize: 99}
    });
    const level2Url = setQueryParams(this.apiUrl, {
      filter: level2UrlFilter,
      navParams: {nPageSize: 99}
    });
    const level3Url = setQueryParams(this.apiUrl, {
      filter: {DEPTH_LEVEL: '3', ACTIVE: 'Y', GLOBAL_ACTIVE: 'Y'},
      navParams: {nPageSize: 999}
    });

    try {
      const level1Promise = this.http.get<CatalogSection[]>(level1Url).toPromise();
      const level2Promise = this.http.get<CatalogSection[]>(level2Url).toPromise();
      const level3Promise = this.http.get<CatalogSection[]>(level3Url).toPromise();

      const sections1lev = await level1Promise;
      const sections2lev = await level2Promise;
      const sections3lev = await level3Promise;

      const filterInit = {ACTIVE: 'Y', 'PROPERTY_TAGS.XML_ID': tags};
      const promisesCounts: Promise<any>[] = [];

      sections1lev.forEach((section, i) => {
        const filter = Object.assign(JSON.parse(JSON.stringify(filterInit)), {'SECTION_ID': section.ID, 'INCLUDE_SUBSECTIONS': 'Y'});
        promisesCounts[i] = this.catalogService.getCount({filter: filter});
      });

      const promisesSections = sections1lev.map<Promise<CatalogSection>>(async (sec1lev: CatalogSection, i: number) => {
        sec1lev.SUBSECTIONS = sections2lev.filter((sec2lev: CatalogSection) => {
          return sec2lev.IBLOCK_SECTION_ID === sec1lev.ID;
        });
        sec1lev.SUBSECTIONS.map<CatalogSection>((sec2lev: CatalogSection) => {
          sec2lev.SUBSECTIONS = sections3lev.filter((sec3lev: CatalogSection) => {
            return sec3lev.IBLOCK_SECTION_ID === sec2lev.ID;
          });
          return sec2lev;
        });

        let count: {count: string};
        try {
          count = await promisesCounts[i];
        } catch (e) {
          count = {count: '0'};
        }
        sec1lev.ELEMENTS_COUNT = count.count;

        return sec1lev;
      });

      return await Promise.all(promisesSections);
    } catch (e) {
      this.handleError(e);
    }
  }

  // if the first argument is similar to a number then get by id
  public getByCode(code: string, params?: ApiQueryParams): Promise<CatalogSection | any> {
    const apiUrl = setQueryParams(`${this.apiUrl}${code}`, params);
    return this.http.get<CatalogSection>(apiUrl, {withCredentials: true})
      .toPromise()
      .catch(this.handleError);
  }
}
